import { useState, useEffect } from 'react'
import { Shield, Plus, X, AlertTriangle } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchEditors, addEditor, removeEditor, clearEditors, isEmailAppEditor } from '../store/editorsSlice'
import './AppEditors.css'

interface AppEditorsProps {
  appId: string
}

function AppEditors({ appId }: AppEditorsProps) {
  const dispatch = useAppDispatch()
  const { items, loading, error } = useAppSelector((state) => state.editors)
  const authUser = useAppSelector((state) => state.auth.user)
  const [showForm, setShowForm] = useState(false)
  const [pattern, setPattern] = useState('')
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    dispatch(fetchEditors(appId))
    return () => {
      dispatch(clearEditors())
    }
  }, [dispatch, appId])

  const isEditor = !!authUser && isEmailAppEditor(authUser.email, items)

  const handleAdd = async () => {
    const trimmed = pattern.trim()
    if (!trimmed) return
    setAdding(true)
    await dispatch(addEditor({ appId, pattern: trimmed }))
    setAdding(false)
    setPattern('')
    setShowForm(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && pattern.trim()) {
      handleAdd()
    }
  }

  const handleRemove = (editorId: number) => {
    dispatch(removeEditor({ appId, editorId }))
  }

  return (
    <section className="app-editors" data-testid="app-editors" aria-labelledby="app-editors-heading">
      <div className="app-editors__header">
        <h2 className="app-editors__title" id="app-editors-heading">
          <Shield size={20} aria-hidden="true" />
          Editors
        </h2>
        {isEditor && (
          <button
            className="app-editors__add-btn"
            onClick={() => setShowForm(!showForm)}
            aria-expanded={showForm}
            data-testid="add-editor-btn"
          >
            <Plus size={16} aria-hidden="true" />
            Add Editor
          </button>
        )}
      </div>
      {error && (
        <div className="app-editors__error" role="alert" data-testid="app-editors-error">
          <AlertTriangle size={14} aria-hidden="true" />
          {error}
        </div>
      )}
      {showForm && (
        <div className="app-editors__form" data-testid="editor-form">
          <label htmlFor="editor-pattern-input" className="sr-only">Editor pattern</label>
          <input
            id="editor-pattern-input"
            className="app-editors__input"
            type="text"
            placeholder="Email or @domain (e.g. user@example.com or @example.com)"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            onKeyDown={handleKeyDown}
            data-testid="editor-pattern-input"
          />
          <button
            className="app-editors__submit-btn"
            onClick={handleAdd}
            disabled={adding || !pattern.trim()}
            data-testid="editor-submit-btn"
          >
            {adding ? 'Adding...' : 'Add'}
          </button>
          <button
            className="app-editors__cancel-btn"
            onClick={() => { setShowForm(false); setPattern('') }}
            data-testid="editor-cancel-btn"
          >
            Cancel
          </button>
        </div>
      )}
      <div className="app-editors__list" role="list" data-testid="editors-list">
        {loading && items.length === 0 && (
          <div className="app-editors__empty" role="status">Loading editors...</div>
        )}
        {!loading && items.length === 0 && (
          <div className="app-editors__empty" data-testid="editors-empty">
            No editors configured — all authenticated users can edit this app.
          </div>
        )}
        {items.map((editor) => (
          <div
            key={editor.id}
            className="app-editors__item"
            role="listitem"
            data-testid="editor-item"
          >
            <span className={`app-editors__pattern${editor.pattern.startsWith('@') ? ' app-editors__pattern--domain' : ''}`}>
              {editor.pattern}
            </span>
            {isEditor && (
              <button
                className="app-editors__remove-btn"
                onClick={() => handleRemove(editor.id)}
                data-testid="editor-remove-btn"
              >
                <X size={14} aria-hidden="true" />
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

export default AppEditors
